import React, { useRef } from 'react';

const MANUAL_UPLOAD_PORTAL = 'portal';
const MANUAL_UPLOAD_MOBILE = 'mobile';
const MANUAL_DOCUMENT_COUNT = 4;

const MANUAL_FILE_STATE_KEYS = {
  contractSigned: 'contract_signed',
  selfie: 'selfie',
  documentFront: 'document_front',
  documentBack: 'document_back'
};

const MANUAL_FILE_REQUIREMENTS = [
  { key: 'contractSigned', label: 'Contrato firmado', icon: 'bi-file-earmark-text' },
  { key: 'selfie', label: 'Selfie', icon: 'bi-person-bounding-box' },
  { key: 'documentFront', label: 'DNI frente', icon: 'bi-card-image' },
  { key: 'documentBack', label: 'DNI dorso', icon: 'bi-card-image' }
];

const MANUAL_HELP_MESSAGE = 'Subí los 4 archivos requeridos. Cuando estén completos, podrás continuar.';

const countUploadedManualDocuments = (manualSignature = {}) => (
  MANUAL_FILE_REQUIREMENTS.filter(({ key }) => Boolean(manualSignature?.[MANUAL_FILE_STATE_KEYS[key]])).length
);

const getManualDocumentsProgressMessage = (manualSignature = {}) => {
  const missing = MANUAL_DOCUMENT_COUNT - countUploadedManualDocuments(manualSignature);
  if (missing <= 0) {
    return 'Documentación completa';
  }
  if (missing === MANUAL_DOCUMENT_COUNT) {
    return MANUAL_HELP_MESSAGE;
  }
  return `Faltan ${missing} documento${missing === 1 ? '' : 's'} para continuar.`;
};

const getDocumentStatus = ({ key, isUploaded, selectedFiles, signatureError }) => {
  if (isUploaded(key)) return 'uploaded';
  if (signatureError && selectedFiles[key]) return 'error';
  return 'pending';
};

const statusLabel = {
  pending: 'Pendiente',
  uploaded: 'Cargado',
  error: 'Error'
};

const PaperSignaturePanel = ({
  manualSignature = {},
  selectedFiles = {},
  signatureError = '',
  isUploading = false,
  onFileSelect,
  onUpload
}) => {
  const inputRefs = useRef({});

  const isUploaded = (key) => Boolean(manualSignature?.[MANUAL_FILE_STATE_KEYS[key]]);
  const uploadedCount = countUploadedManualDocuments(manualSignature);
  const allUploaded = uploadedCount === MANUAL_DOCUMENT_COUNT;
  const hasSelectedFiles = MANUAL_FILE_REQUIREMENTS.some(({ key }) => selectedFiles[key]);
  const canSave = hasSelectedFiles && !isUploading;
  const progressMessage = getManualDocumentsProgressMessage(manualSignature);

  const openFilePicker = (key) => {
    if (isUploading) return;
    inputRefs.current[key]?.click();
  };

  const handleReplaceClick = (key) => (event) => {
    event.stopPropagation();
    openFilePicker(key);
  };

  return (
    <section className="rg-signature-panel rg-signature-panel--manual" aria-label="Firma manual">
      <div className="rg-signature-panel__intro">
        <h5 className="rg-signature-panel__title">Firma manual</h5>
        <p className="rg-signature-panel__description">
          Subí la documentación del cliente desde este portal.
        </p>
      </div>

      <div className="rg-signature-upload-path">
        <div className="rg-signature-upload-path__grid">
          <button
            type="button"
            className="rg-signature-upload-path__card rg-signature-upload-path__card--disabled"
            disabled
            aria-disabled="true"
          >
            <i className="bi bi-phone" aria-hidden="true"></i>
            <span className="rg-signature-upload-path__label">Cargar desde celular</span>
            <span className="rg-signature-upload-path__badge">Próximamente</span>
          </button>
          <div
            className="rg-signature-upload-path__card rg-signature-upload-path__card--selected rg-signature-upload-path__card--static"
            aria-current="true"
          >
            <i className="bi bi-display" aria-hidden="true"></i>
            <span className="rg-signature-upload-path__label">Cargar desde este portal</span>
          </div>
        </div>
      </div>

      <div className="rg-signature-panel__section rg-signature-panel__upload">
        <div className="rg-signature-panel__upload-header">
          <h6 className="rg-signature-panel__subtitle">
            Documentación requerida
            <span className="rg-signature-panel__counter" aria-live="polite">
              {' · '}{uploadedCount}/{MANUAL_DOCUMENT_COUNT} cargados
            </span>
          </h6>
        </div>

        <p
          className={`rg-signature-panel__progress-message ${allUploaded ? 'rg-signature-panel__progress-message--complete' : ''}`}
          role="status"
        >
          {progressMessage}
        </p>

        <div className="rg-signature-doc-grid">
          {MANUAL_FILE_REQUIREMENTS.map(({ key, label, icon }) => {
            const status = getDocumentStatus({ key, isUploaded, selectedFiles, signatureError });
            const file = selectedFiles[key];
            const hasFile = Boolean(file) || isUploaded(key);
            const inputId = `manual-signature-${key}`;
            const tileClassName = `rg-signature-doc-tile rg-signature-doc-tile--${status}`;
            const fileInput = (
              <input
                ref={(el) => { inputRefs.current[key] = el; }}
                id={inputId}
                type="file"
                className="visually-hidden"
                accept="image/*,.pdf"
                disabled={isUploading}
                aria-label={label}
                onChange={(event) => {
                  const fileSelected = event.target.files?.[0];
                  if (fileSelected) onFileSelect?.(key, fileSelected);
                  event.target.value = '';
                }}
              />
            );
            const tileContent = (
              <>
                {fileInput}

                <span className="rg-signature-doc-tile__icon" aria-hidden="true">
                  <i className={`bi ${icon}`}></i>
                </span>

                <div className="rg-signature-doc-tile__body">
                  <div className="rg-signature-doc-tile__title-row">
                    <span className="rg-signature-doc-tile__label">{label}</span>
                    <span className={`rg-signature-doc-tile__status rg-signature-doc-tile__status--${status}`}>
                      {statusLabel[status]}
                    </span>
                  </div>

                  {!hasFile && (
                    <span className="rg-signature-doc-tile__meta">PDF, JPG o PNG hasta 50MB</span>
                  )}

                  {hasFile && (
                    <div className="rg-signature-doc-tile__file-row">
                      <span className="rg-signature-doc-tile__filename">
                        {file?.name || 'Archivo cargado'}
                      </span>
                      <button
                        type="button"
                        className="rg-signature-doc-tile__action rg-signature-doc-tile__action--button"
                        onClick={handleReplaceClick(key)}
                        disabled={isUploading}
                      >
                        Reemplazar
                      </button>
                    </div>
                  )}
                </div>
              </>
            );

            if (hasFile) {
              return (
                <div key={key} className={tileClassName}>
                  {tileContent}
                </div>
              );
            }

            return (
              <label key={key} htmlFor={inputId} className={tileClassName} aria-disabled={isUploading}>
                {tileContent}
              </label>
            );
          })}
        </div>

        <div className="rg-signature-panel__upload-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onUpload}
            disabled={!canSave}
            aria-busy={isUploading}
          >
            {isUploading ? 'Subiendo documentación…' : 'Subir documentación'}
          </button>
        </div>
      </div>

      {signatureError && (
        <div className="rg-signature-panel__error" role="alert">
          {signatureError}
        </div>
      )}
    </section>
  );
};

export {
  MANUAL_UPLOAD_PORTAL,
  MANUAL_UPLOAD_MOBILE,
  MANUAL_FILE_REQUIREMENTS,
  MANUAL_DOCUMENT_COUNT,
  MANUAL_HELP_MESSAGE,
  countUploadedManualDocuments,
  getManualDocumentsProgressMessage
};
export default PaperSignaturePanel;
